import * as stylex from "@stylexjs/stylex";
import {
    type CSSProperties,
    type FunctionComponent,
    type HTMLInputTypeAttribute,
    isValidElement,
    toChildArray,
} from "preact";
import { type ChangeEvent } from "preact/compat";
import { useLayoutEffect, useRef, useState } from "preact/hooks";

import placeholderPic from "../assets/image-placeholder.png";
import Button, { type ButtonProps } from "./Button.tsx";
import NiceModal, { type NiceModalHocProps, useModal } from "@ebay/nice-modal-react";
import Modal from "./Modal.tsx";
import { Cropper, type ReactCropperElement } from "react-cropper";
import asObjectURL from "../utils/asObjectURL.ts";

const styles = stylex.create({
    row: {
        gap: "15px",
        display: "grid",
        gridTemplateColumns: "1fr 2fr",
        margin: "0 15px",
        marginBottom: "15px",
    },
    rowLabel: {
        fontSize: "13px",
        fontFamily: "'Inter Variable', sans-serif",
        textAlign: "right",
        color: "#666",
        paddingTop: "2px",
    },
    inlineGroup: {
        gap: "30px",
        display: "flex",
    },
    input: {
        width: "100%",
        fontSize: "13px",
        fontFamily: "'Inter Variable', sans-serif",
        padding: "5px",
        borderRadius: "2px",
        borderStyle: "solid",
        borderWidth: "1px",
        borderColor: "hwb(0 0 100 / .1)",
        backgroundColor: "#FFF",
        boxShadow: "inset 0px 0px 2px hwb(0 0 100 / .1)",
        ":focus-visible": {
            outline: "1px solid #8EB0FF",
            boxShadow: "0 0 4px 0 #8EB0FF, inset 0px 0px 2px hwb(0 0 100 / .1)",
        },
    },
    inputDisabled: {
        cursor: "no-drop",
        backgroundColor: "hwb(0 0 100 / .05)",
        boxShadow: "none",
        borderColor: "hwb(0 0 100 / .05)",
        resize: "none",
    },
    textArea: {
        resize: "none",
    },
    checkBox: {
        position: "relative",
        appearance: "none",
        height: "14px",
        width: "14px",
        backgroundColor: "#f6f6f6",
        borderStyle: "solid",
        borderWidth: "1px",
        borderColor: "#8e8f8f",
        transition: ".4s ease-in-out",
        boxShadow:
            "inset 0 0 0 1px #f4f4f4, inset 1px 1px 0 1px #aeaeae, inset -1px -1px 0 1px #ddd, inset 3px 3px 6px #ccc",
        cursor: "pointer",
        ":hover:not([disabled])": {
            backgroundColor: "#e9f7fe",
            borderColor: "#3c7fb1",
            boxShadow:
                "inset 0 0 0 1px #def9fa, inset 1px 1px 0 1px #79c6f9, inset -1px -1px 0 1px #c6e9fc, inset 3px 3px 6px #b1dffd",
        },
        ":checked::after": {
            position: "absolute",
            color: "#4a5f97",
            content: "\\2714",
            display: "block",
            fontFamily: "'Arial', 'Inter Variable', sans-serif",
            fontWeight: 700,
            fontSize: "12px",
            left: 2,
            top: -2,
        },
    },
    checkBoxDisabled: {
        backgroundImage: "linear-gradient(to bottom right, #f0f0f0, #fbfbfb)",
        borderColor: "#b1b1b1",
        boxShadow: "none",
        cursor: "no-drop",
        ":checked::after": {
            filter: "grayscale(1) brightness(1.3)",
        },
    },
    checkBoxIconDisabled: {
        filter: "grayscale(1) opacity(.7)",
    },
    radioButton: {
        borderRadius: "100%",
        ":checked::after": {
            content: "",
            backgroundColor: "#7cd3eb",
            borderWidth: 1.5,
            borderColor: "#27506d",
            borderStyle: "solid",
            borderRadius: "100%",
            boxShadow: "inset -1px -1px 0 .5px #16638f, inset -1px -1px 0 1px #1985c0",
            height: 7,
            width: 7,
            left: "18%",
            top: "18%",
        },
    },
    checkBoxRow: {
        gap: 6,
        display: "grid",
        alignItems: "flex-start",
        gridTemplateColumns: "max-content 1fr",
        color: "#666",
        fontSize: "13px",
        fontFamily: "'Inter Variable', sans-serif",
        cursor: "pointer",
    },
    checkBoxRowDisabled: {
        cursor: "unset",
    },
    checkBoxLabel: {
        display: "block",
        textAlign: "left",
        paddingTop: 1,
    },
    imagePreviewContainer: {
        gap: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
    },
    imagePreview: {
        objectFit: "contain",
        boxShadow: "0 0 2px 0 color-mix(in prophoto-rgb, #000 40%, transparent)",
        borderRadius: "4px",
        backgroundColor: "#dae4ed",
    },
    imageCropActions: {
        gap: 10,
        marginTop: 15,
        display: "flex",
        justifyContent: "center",
    },
});

interface InputProps {
    type?: Exclude<
        HTMLInputTypeAttribute,
        "submit" | "range" | "radio" | "image" | "hidden" | "file" | "color" | "checkbox" | "button"
    >;
    placeholder?: string;
    disabled?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    value?: string | number;
    style?: CSSProperties;
}

const Input: FunctionComponent<InputProps> = ({
    type = "text",
    placeholder,
    disabled = false,
    onChange = () => {},
    style = {},
    value,
}) => {
    return (
        <input
            {...stylex.props(styles.input, disabled && styles.inputDisabled)}
            style={style}
            onChange={onChange}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            value={value}
        />
    );
};

type TextareaProps = Omit<Omit<InputProps, "type">, "onChange"> & {
    height?: number;
    onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
};

const TextArea: FunctionComponent<TextareaProps> = ({
    placeholder,
    height = 50,
    disabled = false,
    onChange = (_) => {},
    children,
}) => {
    const area = useRef<HTMLTextAreaElement>(null);
    const dispatchChange = (e: ChangeEvent<HTMLTextAreaElement> | null) => {
        requestAnimationFrame(() => {
            area!.current!.style.height = "5px";
            area!.current!.style.height = Math.max(height, area!.current!.scrollHeight + 2) + "px";
        });

        if (e) onChange(e);
    };

    useLayoutEffect(() => dispatchChange(null), []);

    return (
        <textarea
            {...stylex.props(styles.input, styles.textArea, disabled && styles.inputDisabled)}
            ref={area}
            onChange={dispatchChange}
            placeholder={placeholder}
            disabled={disabled}
            style={{ height: `${height}px` }}
        >
            {children}
        </textarea>
    );
};

interface CheckBoxProps {
    label: string;
    icon?: string;
    iconSize?: number;
    disabled?: boolean;
    checked?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

const CheckBox: FunctionComponent<CheckBoxProps> = ({
    label,
    icon,
    iconSize = 32,
    disabled = false,
    checked = false,
    onChange = () => {},
}) => {
    return (
        <label {...stylex.props(styles.checkBoxRow, disabled && styles.checkBoxRowDisabled)}>
            <input
                {...stylex.props(styles.checkBox, disabled && styles.checkBoxDisabled)}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={onChange}
            />
            {icon && (
                <img
                    {...stylex.props(disabled && styles.checkBoxIconDisabled)}
                    src={icon}
                    alt={label}
                    width={iconSize}
                    draggable={false}
                />
            )}
            <span {...stylex.props(styles.checkBoxLabel)}>{label}</span>
        </label>
    );
};

type RadioProps = CheckBoxProps & { name: string };

const Radio: FunctionComponent<RadioProps> = ({
    name,
    label,
    icon,
    iconSize = 32,
    disabled = false,
    checked = false,
    onChange = () => {},
}) => {
    return (
        <label {...stylex.props(styles.checkBoxRow, disabled && styles.checkBoxRowDisabled)}>
            <input
                {...stylex.props(
                    styles.checkBox,
                    styles.radioButton,
                    disabled && styles.checkBoxDisabled,
                )}
                type="radio"
                name={name}
                checked={checked}
                disabled={disabled}
                onChange={onChange}
            />
            {icon && (
                <img
                    {...stylex.props(disabled && styles.checkBoxIconDisabled)}
                    src={icon}
                    alt={label}
                    width={iconSize}
                    draggable={false}
                />
            )}
            <span {...stylex.props(styles.checkBoxLabel)}>{label}</span>
        </label>
    );
};

const Row: FunctionComponent<{ label?: string }> = ({ label = "", children }) => (
    <div {...stylex.props(styles.row)}>
        <div {...stylex.props(styles.rowLabel)}>
            <span>{label && <>{label}:</>}</span>
        </div>
        <div>{children}</div>
    </div>
);

const InlineControlGroup: FunctionComponent = ({ children }) => (
    <div {...stylex.props(styles.inlineGroup)}>
        {toChildArray(children).filter(
            (child) => isValidElement(child) && (child.type == CheckBox || child.type == Radio),
        )}
    </div>
);

interface ImageCropModalProps {
    url: string;
    aspectRatio?: number | null;
    minWidth?: number;
    minHeight?: number;
}

const ImageCropModal: FunctionComponent<ImageCropModalProps & NiceModalHocProps> = NiceModal.create(
    ({
        url,
        aspectRatio = null,
        minWidth = 100,
        minHeight = aspectRatio ? minWidth / aspectRatio : minWidth,
    }) => {
        const modal = useModal();
        if (!modal) throw new Error("ImageCropModal used outside of modal context");

        const cropper = useRef<ReactCropperElement>(null);
        const doCrop = () => {
            const canvas = cropper.current!.cropper.getCroppedCanvas();
            canvas.toBlob((blob) => {
                modal.resolve([blob, canvas.toDataURL("image/webp")]);
                modal.remove();
            }, "image/webp");
        };

        return (
            <Modal.Abstract closable>
                <Cropper
                    ref={cropper}
                    src={url}
                    style={{ width: "420px", height: "400px", overflowX: "hidden" }}
                    guides={false}
                    center={false}
                    viewMode={1}
                    dragMode="move"
                    highlight={false}
                    aspectRatio={aspectRatio || undefined}
                    minCropBoxWidth={minWidth}
                    minCropBoxHeight={minHeight}
                    toggleDragModeOnDblclick={false}
                    checkOrientation={false}
                />

                <div {...stylex.props(styles.imageCropActions)}>
                    <Button label="Сохранить" kind="success" onClick={doCrop} />
                    <Button
                        label="Отменить"
                        kind="danger"
                        onClick={() => {
                            modal.resolve(null);
                            modal.remove();
                        }}
                    />
                </div>
            </Modal.Abstract>
        );
    },
);

export type CustomFileProvider = Omit<ButtonProps, "onClick"> & {
    getFile: () => Promise<File | null>;
};

interface ImageInputProps {
    label: string;
    accepts?: string;
    previewWidth?: number;
    previewHeight?: number | null;
    aspectRatio?: number | null;
    minWidth?: number;
    disabled?: boolean;
    initialImage?: Blob | string | null;
    fileProviders?: CustomFileProvider[];
    actionButtonStyle?: CSSProperties;
    onImageBlob: (img: Blob) => void;
}

const ImageInput: FunctionComponent<ImageInputProps> = ({
    label,
    onImageBlob,
    disabled = false,
    accepts = "image/*",
    actionButtonStyle = {},
    previewWidth = 64,
    previewHeight = 64,
    aspectRatio = null,
    initialImage = null,
    fileProviders = [],
    minWidth = 100,
}) => {
    previewHeight = aspectRatio ? previewWidth / aspectRatio : previewHeight;

    const [blob, setBlob] = useState<Blob | null>(null);
    const [previewUrl, setPreviewUrl] = useState(placeholderPic);
    if (null != initialImage) {
        if (typeof initialImage == "string") {
            setPreviewUrl(initialImage);
        } else {
            setBlob(initialImage);
            setPreviewUrl(asObjectURL(initialImage));
        }
    }

    const cropImage = async (image: File) => {
        const reader = new FileReader();
        reader.onload = async () => {
            if (!reader.result) return;

            const croppedBlob = (await NiceModal.show(ImageCropModal, {
                url: reader.result as string,
                aspectRatio,
                minWidth,
            })) as [Blob, string] | null;

            if (!croppedBlob) return;

            if (blob) URL.revokeObjectURL(previewUrl);

            setBlob(croppedBlob[0]);
            onImageBlob(croppedBlob[0]);
            setPreviewUrl(croppedBlob[1]);
        };

        reader.readAsDataURL(image);
    };

    const fileInput = useRef<HTMLInputElement>(null);
    const pickPicture = () => fileInput.current!.click();
    const filePicked = () => {
        if (!fileInput.current!.files || !fileInput.current!.files[0]) return;

        cropImage(fileInput.current!.files[0]);
    };

    const getFileFromProvider = async (provider: CustomFileProvider) => {
        const file = await provider.getFile();
        if (!file) return;

        cropImage(file);
    };

    return (
        <div {...stylex.props(styles.imagePreviewContainer)}>
            <div>
                <Button
                    label={label}
                    disabled={disabled}
                    onClick={pickPicture}
                    style={{ marginRight: 10, ...actionButtonStyle }}
                />

                {fileProviders.map((provider) => (
                    <Button
                        key={provider.label}
                        label={provider.label}
                        kind={provider.kind}
                        disabled={provider.disabled}
                        style={{ ...provider.style, marginRight: 10 }}
                        onClick={() => getFileFromProvider(provider)}
                    />
                ))}
            </div>
            <input
                ref={fileInput}
                type="file"
                style={{ display: "none" }}
                accept={accepts}
                onChange={filePicked}
            />
            <img
                {...stylex.props(styles.imagePreview)}
                alt="Preview"
                src={previewUrl}
                draggable={false}
                style={{ width: previewWidth, height: previewHeight }}
            />
        </div>
    );
};

export default { Row, InlineControlGroup, Input, TextArea, CheckBox, Radio, ImageInput };
